export interface DrawerHandle {
  readonly element: HTMLElement;
  setTitle(title: string): void;
  setContent(node: HTMLElement): void;
  open(): void;
  close(): void;
  isOpen(): boolean;
}

export interface DrawerProps {
  readonly title: string;
  readonly onClose?: () => void;
}

// 右スライドオーバー。Esc/背景クリックで閉じ、開いている間は Tab フォーカスをパネル内に閉じ込める(WCAG2)。
export function createDrawer(props: DrawerProps): DrawerHandle {
  let open = false;
  let lastFocused: HTMLElement | null = null;

  const root = document.createElement("div");
  root.className = "fixed inset-0 z-40 hidden";
  root.setAttribute("aria-hidden", "true");

  const backdrop = document.createElement("div");
  backdrop.className = "absolute inset-0 bg-slate-900/30";

  const panel = document.createElement("div");
  panel.className =
    "absolute right-0 top-0 h-full w-full max-w-md overflow-y-auto bg-white shadow-xl";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "true");
  panel.setAttribute("aria-label", props.title);

  const header = document.createElement("div");
  header.className = "flex items-center justify-between border-b border-slate-200 px-5 py-4";
  const titleEl = document.createElement("h2");
  titleEl.className = "text-lg font-medium text-slate-900";
  titleEl.textContent = props.title;
  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "rounded p-1 text-slate-500 hover:bg-slate-100";
  closeBtn.setAttribute("aria-label", "閉じる");
  closeBtn.textContent = "×"; // U+00D7(絵文字ではない)
  header.append(titleEl, closeBtn);

  const body = document.createElement("div");
  body.className = "px-5 py-4";

  panel.append(header, body);
  root.append(backdrop, panel);

  function trap(e: KeyboardEvent): void {
    const f = panel.querySelectorAll<HTMLElement>(
      'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])',
    );
    if (f.length === 0) return;
    const first = f[0]!;
    const last = f[f.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
  function onKeydown(e: KeyboardEvent): void {
    if (e.key === "Escape") api.close();
    else if (e.key === "Tab") trap(e);
  }

  const api: DrawerHandle = {
    element: root,
    setTitle(title) {
      titleEl.textContent = title;
      panel.setAttribute("aria-label", title);
    },
    setContent(node) {
      body.replaceChildren(node);
    },
    open() {
      if (open) return;
      open = true;
      lastFocused = document.activeElement as HTMLElement | null;
      root.classList.remove("hidden");
      root.setAttribute("aria-hidden", "false");
      document.addEventListener("keydown", onKeydown);
      closeBtn.focus();
    },
    close() {
      if (!open) return;
      open = false;
      root.classList.add("hidden");
      root.setAttribute("aria-hidden", "true");
      document.removeEventListener("keydown", onKeydown);
      lastFocused?.focus();
      props.onClose?.();
    },
    isOpen() {
      return open;
    },
  };

  backdrop.addEventListener("click", () => api.close());
  closeBtn.addEventListener("click", () => api.close());
  return api;
}
